import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Page } from "../api/admin";
import type { ApiError } from "../api/client";
import { TableSkeleton } from "./Loading";
import { Pager } from "./Pager";
import { ExportButton } from "./ExportButton";
import { usePage } from "../lib/usePage";
import { formatNumber } from "../lib/format";

/**
 * One column.
 *
 * `labela` is what the cell is called when the row becomes a card on a phone; it is required because
 * a stacked row without labels is a column of bare values, which is worse than scrolling sideways.
 * `broj` marks a figure: right-aligned and with tabular numerals, so digits line up down the column.
 */
export type Kolona<T> = {
  kljuc: string;
  zaglavlje: ReactNode;
  labela: string;
  crtaj: (red: T) => ReactNode;
  broj?: boolean;
  /** An actions cell: no label on a phone, and its buttons stretch to the full width. */
  radnje?: boolean;
  /**
   * The row's headline: the cell a reader looks at first and the one they carry away — a lot mark,
   * a contract number, an invoice number. On a narrow screen it keeps the full width and drops its
   * label, because a label above the thing that names the row is noise: the row IS that thing.
   */
  naslovna?: boolean;
};

type Props<T> = {
  naslov: string;
  podnaslov?: string;
  /** The filter bar. Pass `FinanceFilters` or the screen's own controls — this only places it. */
  filteri?: ReactNode;
  /**
   * One page of rows. Takes the page index and size and nothing else: the filters live in the
   * caller's closure, and a change in them must change `kljucFiltera`.
   */
  ucitaj: (strana: number, velicina: number) => Promise<Page<T>>;
  /**
   * The filters as text. The register re-reads when it changes and returns to the first page, so a
   * caller cannot forget to — the defect that made a narrowed filter answer on page seven and look
   * like no matches.
   */
  kljucFiltera?: string;
  /** True while any filter is set. Decides which of the two empty messages is the true one. */
  filtrirano?: boolean;
  kolone: Kolona<T>[];
  kljucReda: (red: T) => string;
  /** Makes the first cell a link and the row a `row-link`. */
  vezaNaRed?: (red: T) => string;
  /** CSV path and filename. The caller builds the path from the SAME filters as `ucitaj`. */
  izvoz?: { putanja: string; ime: string };
  /** Shown beside the row count — for what the register deliberately leaves out. */
  napomena?: ReactNode;
  /** When nothing is in the register at all. */
  prazno?: string;
  /** When a filter matches nothing. Different from the above, and never the same sentence. */
  praznoUzFilter?: string;
  velicina?: number;
};

const VELICINA = 25;

/**
 * A register: a filtered, paged list of rows, with its export.
 *
 * <p>It exists because the same decisions were being taken separately on every list screen, and a
 * decision taken separately is a decision taken differently. Measured across the screens that draw a
 * table: five carried a class the stylesheet does not define, three drew the table on the bare page,
 * three never re-read when the reader returned to the first page, and one computed the figures above
 * the table from the fifty rows the open page happened to hold — showing fifty where the truth was
 * ten thousand. None of that came from not knowing; it came from the standard existing only as other
 * screens to imitate.
 *
 * <p>So the parts a reader can be misled by are not the caller's to make. The page comes from the
 * server. The count beside the table is the server's total, never the loaded page's length. A stale
 * answer cannot overwrite a newer one. A failure is cleared by the next request rather than hiding
 * the register for good. The first open draws a skeleton; a page change keeps the rows and dims them,
 * so the reader keeps their place. The pager stands above and below. The export carries the filters
 * the table carries. On a phone each row becomes a labelled card, because a row whose action is off
 * the right edge is a row nobody uses.
 *
 * <p>What the caller brings is what only the caller knows: the columns, the filters, where a row
 * leads, and the two different sentences for an empty register and an empty filter.
 */
export function Registar<T>({
  naslov,
  podnaslov,
  filteri,
  ucitaj,
  kljucFiltera = "",
  filtrirano = false,
  kolone,
  kljucReda,
  vezaNaRed,
  izvoz,
  napomena,
  prazno,
  praznoUzFilter,
  velicina = VELICINA,
}: Props<T>) {
  const { t } = useTranslation();
  const [strana, postaviStranu] = usePage();
  const [podaci, setPodaci] = useState<Page<T> | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [osvezava, setOsvezava] = useState(false);

  // A different question is answered from the first page. Without this a narrowed filter answers on
  // page seven and shows nothing, which the reader reads as "no matches".
  const prethodniKljuc = useRef(kljucFiltera);
  useEffect(() => {
    if (prethodniKljuc.current === kljucFiltera) return;
    prethodniKljuc.current = kljucFiltera;
    postaviStranu(0);
  }, [kljucFiltera, postaviStranu]);

  // Every request carries a number and only the newest may write to the screen. Two are in the air
  // whenever a filter changes while an answer is still on its way, and the older one may land last.
  const izdanje = useRef(0);
  const ucitajRef = useRef(ucitaj);
  ucitajRef.current = ucitaj;

  useEffect(() => {
    const moje = (izdanje.current += 1);
    setOsvezava(true);
    setGreska(null);
    ucitajRef
      .current(strana, velicina)
      .then((p) => {
        if (moje === izdanje.current) setPodaci(p);
      })
      .catch((e: ApiError) => {
        if (moje === izdanje.current) setGreska(e.message);
      })
      .finally(() => {
        if (moje === izdanje.current) setOsvezava(false);
      });
  }, [strana, velicina, kljucFiltera]);

  const redovi = podaci?.content ?? [];
  const ukupno = podaci?.totalElements ?? 0;
  const kostur = podaci === null && !greska;

  const stranicar = useMemo(
    () => <Pager page={strana} size={velicina} total={ukupno} busy={osvezava} onPage={postaviStranu} />,
    [strana, velicina, ukupno, osvezava, postaviStranu],
  );

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{naslov}</h1>
          {podnaslov && <div className="sub">{podnaslov}</div>}
        </div>
        {(filteri || izvoz) && (
          <div className="page-actions page-filteri">
            {filteri}
            {izvoz && (
              <ExportButton
                path={izvoz.putanja}
                label={t("common.exportCsv", "CSV")}
                filename={izvoz.ime}
              />
            )}
          </div>
        )}
      </div>

      {greska && <div className="empty">{greska}</div>}

      {/* The total stays even when the pager hides itself below one page: a filter narrowing the
          register to twenty rows would otherwise hide the count exactly when it is being asked. */}
      {!kostur && !greska && (
        <div className="sub">
          {t("common.rowCount", "{{n}} rows", { n: formatNumber(ukupno) })}
          {napomena && <> · {napomena}</>}
        </div>
      )}

      {kostur ? (
        <TableSkeleton rows={8} cols={kolone.length} label={t("common.loading", "Učitavam…")} />
      ) : !greska && redovi.length === 0 ? (
        // Two different facts, never the same sentence: the register holds nothing, or this filter
        // matches nothing. Told as one, the reader stops looking for the second.
        <div className="empty">
          {filtrirano
            ? praznoUzFilter ?? t("common.noMatches", "Nothing matches this filter.")
            : prazno ?? t("common.emptyRegister", "Nothing here yet.")}
        </div>
      ) : (
        !greska && (
          <>
            {stranicar}
            <div
              className="card registar-karta"
              style={{
                padding: 0,
                opacity: osvezava ? 0.55 : 1,
                transition: "opacity .12s",
              }}
            >
              <table className="tbl kartice">
                <thead>
                  <tr>
                    {kolone.map((k) => (
                      <th key={k.kljuc} style={k.broj ? { textAlign: "right" } : undefined}>
                        {k.zaglavlje}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {redovi.map((red) => {
                    const veza = vezaNaRed?.(red);
                    return (
                      <tr key={kljucReda(red)} className={veza ? "row-link" : undefined}>
                        {kolone.map((k, i) => (
                          <td
                            key={k.kljuc}
                            data-label={k.radnje || k.naslovna ? undefined : k.labela}
                            className={[
                              k.broj ? "num" : "",
                              k.radnje ? "card-actions" : "",
                              k.naslovna ? "card-naslov" : "",
                            ]
                              .filter(Boolean)
                              .join(" ") || undefined}
                          >
                            {veza && i === 0 ? (
                              <Link to={veza} style={{ color: "var(--brand-primary)", textDecoration: "none" }}>
                                {k.crtaj(red)}
                              </Link>
                            ) : (
                              k.crtaj(red)
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {stranicar}
          </>
        )
      )}
    </div>
  );
}
